import typography from "src/theme/typography";
import { Button, styled, buttonClasses } from "@mui/material";
import { BUTTON_VARIANTS, BUTTON_COLORS, BUTTON_SIZES, ICON_COLOR_MODES } from "@hyro-components/hyro-button/HyroButton.consts";

const getVariantStyles = (theme) => ({
  [BUTTON_VARIANTS.CONTAINED]: {
    [BUTTON_COLORS.PRIMARY]: {
      backgroundColor: theme.palette.primary[500],
      color: theme.palette.system[0],
      hoverColor: theme.palette.system[0],
      hoverBackgroundColor: theme.palette.primary[600],
      disabledBackgroundColor: theme.palette.system[300],
      disabledColor: theme.palette.text[600]
    },
    [BUTTON_COLORS.SECONDARY]: {
      backgroundColor: theme.palette.secondary[500],
      color: theme.palette.system[0],
      hoverColor: theme.palette.system[0],
      hoverBackgroundColor: theme.palette.secondary[600],
      disabledBackgroundColor: theme.palette.system[300],
      disabledColor: theme.palette.text[600]
    },
    [BUTTON_COLORS.ERROR]: {
      backgroundColor: theme.palette.error[500],
      color: theme.palette.system[0],
      hoverColor: theme.palette.system[0],
      hoverBackgroundColor: theme.palette.error[600],
      disabledBackgroundColor: theme.palette.system[300],
      disabledColor: theme.palette.text[600]
    }
  },
  [BUTTON_VARIANTS.OUTLINED]: {
    [BUTTON_COLORS.PRIMARY]: {
      backgroundColor: theme.palette.system[0],
      color: theme.palette.primary[500],
      border: `1px solid ${theme.palette.secondary[500]}`,
      hoverColor: theme.palette.primary[500],
      hoverBorder: `1px solid ${theme.palette.secondary[500]}`,
      hoverBackgroundColor: theme.palette.tertiary[300],
      disabledBackgroundColor: theme.palette.system[0],
      disabledBorder: `1px solid ${theme.palette.system[200]}`,
      disabledColor: theme.palette.text[600]
    },
    [BUTTON_COLORS.SECONDARY]: {
      backgroundColor: theme.palette.system[0],
      color: theme.palette.text[700],
      border: `1px solid ${theme.palette.system[400]}`,
      hoverColor: theme.palette.text[700],
      hoverBorder: `1px solid ${theme.palette.secondary[500]}`,
      hoverBackgroundColor: theme.palette.tertiary[300],
      disabledBackgroundColor: theme.palette.system[0],
      disabledBorder: `1px solid ${theme.palette.system[200]}`,
      disabledColor: theme.palette.text[600]
    }
  },
  [BUTTON_VARIANTS.TEXT]: {
    [BUTTON_COLORS.PRIMARY]: {
      backgroundColor: "transparent",
      color: theme.palette.primary[500],
      hoverColor: theme.palette.primary[500],
      hoverBackgroundColor: theme.palette.tertiary[300],
      disabledBackgroundColor: "transparent",
      disabledColor: theme.palette.text[600]
    },
    [BUTTON_COLORS.SECONDARY]: {
      backgroundColor: "transparent",
      color: theme.palette.text[700],
      hoverColor: theme.palette.text[700],
      hoverBackgroundColor: theme.palette.tertiary[300],
      disabledBackgroundColor: "transparent",
      disabledColor: theme.palette.text[600]
    }
  }
});

const getSizeStyles = () => ({
  [BUTTON_SIZES.SMALL]: {
    ...typography.buttonSmall,
    height: "26px",
    padding: "6px 12px",
    iconSize: "14px",
    strokeWidth: "1.4"
  },
  [BUTTON_SIZES.SEMI_MEDIUM]: {
    ...typography.buttonMedium,
    height: "32px",
    padding: "8px 14px",
    iconSize: "18px",
    strokeWidth: "1.6"
  },
  [BUTTON_SIZES.MEDIUM]: {
    ...typography.buttonMedium,
    height: "36px",
    padding: "8px 16px",
    iconSize: "18px",
    strokeWidth: "1.8"
  },
  [BUTTON_SIZES.SEMI_LARGE]: {
    ...typography.buttonMedium,
    height: "40px",
    padding: "10px 16px",
    iconSize: "18px",
    strokeWidth: "1.8"
  },
  [BUTTON_SIZES.LARGE]: {
    ...typography.buttonLarge,
    height: "44px",
    padding: "10px 16px",
    iconSize: "20px",
    strokeWidth: "2"
  }
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "iconColorMode" && prop !== "hasItems"
})(({ theme, variant, color, size, active, iconColorMode }) => {
  const variantStyle = getVariantStyles(theme)[variant][color];
  const sizeStyle = getSizeStyles()[size];

  return {
    ...variantStyle,
    ...sizeStyle,
    boxShadow: "none",
    [`& .${buttonClasses.startIcon} svg, & .${buttonClasses.endIcon} svg`]: {
      width: sizeStyle.iconSize,
      height: sizeStyle.iconSize,
      "& path": {
        strokeWidth: sizeStyle.strokeWidth,
        ...(iconColorMode === ICON_COLOR_MODES.FILL && {
          fill: "currentColor"
        }),
        ...(iconColorMode === ICON_COLOR_MODES.STROKE && {
          stroke: "currentColor"
        })
      }
    },
    "&:hover": {
      backgroundColor: variantStyle.hoverBackgroundColor,
      border: variantStyle.hoverBorder,
      color: variantStyle.hoverColor
    },
    "&:focus-visible": {
      outline: `1px solid ${theme.palette.primary[500]}`,
      outlineOffset: "2px",
      "& .MuiTouchRipple-root": {
        display: "none"
      }
    },
    ...(active && {
      backgroundColor: variantStyle.hoverBackgroundColor,
      border: variantStyle.hoverBorder,
      color: variantStyle.hoverColor
    }),
    [`&.${buttonClasses.disabled}:not(.${buttonClasses.loading})`]: {
      backgroundColor: variantStyle.disabledBackgroundColor,
      color: variantStyle.disabledColor,
      ...(variantStyle.disabledBorder && { border: variantStyle.disabledBorder })
    }
  };
});

export { StyledButton };
